/**
 * Judge Calls · EVALUATIONS (final audit P1-8). The evaluation is the unit an operator debugs:
 * which criteria version and rubric, which prompt contract, which model(s) — fallbacks included —
 * what each competency's verdict was, which quotes support it, what validation changed, and the raw
 * call(s) behind every competency. Nothing here has to be reconstructed from raw JSON.
 *
 * Data: GET /admin/judge-calls → `evaluationDetails` (the stored evaluations) and `calls`
 * (each carrying `evaluationId` and, for an isolated evaluation, the `competencyId` it judged).
 */
import { useState, type ReactNode } from "react";
import { Modal } from "./adminUi.js";

export interface EvalCall {
  id: string;
  provider: string;
  model: string;
  request: unknown;
  response: unknown;
  ok: boolean;
  error: string | null;
  latencyMs: number | null;
  createdAt: string;
  stepRunId?: string | null;
  evaluationId?: string | null;
  competencyId?: string | null;
}

interface CompetencyView {
  competencyId: string;
  label: string;
  weight: number;
  status: "evidenced" | "insufficient_evidence";
  score: number | null;
  evidenceLevel: string;
  confidence: number;
  summary: string;
  evidence: { questionId: string; seq: number | null; strength: string; span: string }[];
}

export interface EvaluationDetail {
  id: string;
  status: "succeeded" | "failed";
  isCurrent: boolean;
  trigger: string;
  assessmentSetVersion: number;
  outdated: boolean;
  setReason: string | null;
  rubric: { id: string; label: string; anchors: string; weight: number; source: string }[];
  capability: number | null;
  coverage: number | null;
  competencies: CompetencyView[];
  recommendation: string | null;
  error: string | null;
  judgeProvider: string | null;
  judgeModel: string | null;
  judgeModelResolved: string | null;
  judgeModels: { provider: string; model: string; resolvedModel: string | null; calls: number; failed: number }[];
  promptSha256: string | null;
  contractVersion: number | null;
  validationNotes: string[];
  textSource: string | null;
  derivedFrom: string | null;
  reason: string | null;
  triggeredBy: string | null;
  createdAt: string;
  madeCurrentAt: string | null;
}

const pretty = (v: unknown): string => {
  try {
    return JSON.stringify(v, null, 2) ?? "";
  } catch {
    return String(v);
  }
};

function Raw({ title, value }: { title: string; value: unknown }): ReactNode {
  return (
    <details style={{ marginTop: 6 }}>
      <summary className="sm" style={{ cursor: "pointer", fontWeight: 600 }}>{title}</summary>
      <pre className="jc-pre">{pretty(value)}</pre>
    </details>
  );
}

const pct = (n: number | null): string => (n == null ? "—" : `${Math.round(n * 100)}%`);

/** The state badge and the plain-words meaning of an evaluation's place in history. */
function standing(e: EvaluationDetail): { label: string; cls: string } {
  if (e.status === "failed") return { label: "Failed — never current", cls: "pill r" };
  if (e.isCurrent) return { label: e.outdated ? "Current (criteria since changed)" : "Current — what the recruiter sees", cls: "pill v" };
  return { label: "Historical", cls: "pill n" };
}

function CompetencyCard({ c, calls, rubric }: { c: CompetencyView; calls: EvalCall[]; rubric: string | undefined }): ReactNode {
  const failed = calls.filter((x) => !x.ok);
  const models = [...new Set(calls.map((x) => `${x.provider}/${x.model}`))];
  const fellBack = models.length > 1 || failed.length > 0;
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="card-h" style={{ gap: 8, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>{c.label}</h3>
        <span className="tag">weight {c.weight}</span>
        <span className={c.status === "evidenced" ? "pill v" : "pill n"}>{c.status === "evidenced" ? "evidenced" : "insufficient evidence"}</span>
        <span className="tnum">{c.score == null ? "no score" : `score ${c.score}`}</span>
        <span className="pill n">evidence: {c.evidenceLevel}</span>
        <span className="mut sm">confidence {Math.round(c.confidence * 100)}%</span>
      </div>
      <div className="card-b">
        <div className="sm">{c.summary}</div>
        {c.evidence.length === 0 ? (
          <div className="mut sm" style={{ marginTop: 6 }}>No quote survived validation for this competency.</div>
        ) : (
          <ul style={{ margin: "8px 0 0 16px", padding: 0 }}>
            {c.evidence.map((e, i) => (
              <li key={i} className="sm" style={{ marginBottom: 4 }}>
                <span className="pill n">{e.strength}</span> <span className="mut">Q{e.seq ?? "?"}</span> “{e.span}”
              </li>
            ))}
          </ul>
        )}
        {rubric && <Raw title="Rubric this competency was judged against" value={rubric} />}
        <div className="sm" style={{ marginTop: 8, fontWeight: 600 }}>
          Model calls ({calls.length}){fellBack ? <span style={{ color: "var(--warn)" }}> · fallback / retry involved</span> : null}
        </div>
        {calls.length === 0 && <div className="mut sm">No raw call is attributed to this competency (single-call evaluation, a derived re-weighting, or recorded before 0099).</div>}
        {calls.map((x, i) => (
          <div key={x.id} style={{ borderTop: "1px solid var(--line-soft)", marginTop: 6, paddingTop: 4 }}>
            <div className="sm">
              #{i + 1} · {x.provider}/{x.model} ·{" "}
              <span style={{ color: x.ok ? "var(--good)" : "var(--crit)" }}>{x.ok ? "ok" : `failed — ${x.error ?? ""}`}</span>
              {x.latencyMs != null ? ` · ${x.latencyMs}ms` : ""}
            </div>
            <Raw title="Request (system prompt + user turn, exactly as sent)" value={x.request} />
            <Raw title="Raw response (before validation)" value={x.response} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function EvaluationInspector({ evaluation: e, calls, onClose }: { evaluation: EvaluationDetail; calls: EvalCall[]; onClose: () => void }): ReactNode {
  const mine = calls.filter((c) => c.evaluationId === e.id);
  const rubricOf = new Map(e.rubric.map((r) => [r.id, r.anchors]));
  const claimed = new Set<string>();
  const st = standing(e);
  const facts: [string, string][] = [
    ["Standing", st.label],
    ["Trigger", e.trigger.replace(/_/g, " ") + (e.derivedFrom ? ` (derived from ${e.derivedFrom.slice(0, 8)}…, no model call)` : "")],
    ["Criteria", `v${e.assessmentSetVersion}${e.outdated ? " — older than the requirement's current version" : ""}${e.setReason ? ` · ${e.setReason}` : ""}`],
    ["Capability / coverage", `${e.capability ?? "—"} / ${pct(e.coverage)} (coverage = weight backed by concrete evidence)`],
    ["Provider", e.judgeProvider ?? "—"],
    ["Model", `${e.judgeModel ?? "—"}${e.judgeModelResolved && e.judgeModelResolved !== e.judgeModel ? ` (${e.judgeModelResolved})` : ""}`],
    ["Prompt", e.promptSha256 ? `sha256:${e.promptSha256.slice(0, 12)} · evaluation contract ${e.contractVersion ?? "legacy"}` : "—"],
    ["Evidence text", e.textSource ?? "—"],
    ["Requested by", `${e.triggeredBy ?? "system"}${e.reason ? ` — ${e.reason}` : ""}`],
    ["When", `${new Date(e.createdAt).toLocaleString()}${e.madeCurrentAt ? ` · current since ${new Date(e.madeCurrentAt).toLocaleString()}` : ""}`],
  ];
  return (
    <Modal title={`Evaluation · v${e.assessmentSetVersion} · ${new Date(e.createdAt).toLocaleString()}`} onClose={onClose} maxWidth={980}>
      <div className="jc-facts">
        {facts.map(([k, v]) => (
          <div key={k}>
            <div className="mut sm">{k}</div>
            <div className="sm" style={{ wordBreak: "break-word" }}>{v}</div>
          </div>
        ))}
      </div>
      {e.error && <div className="banner crit" style={{ marginTop: 8 }}><span>{e.error}</span></div>}
      {e.judgeModels.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="sm" style={{ fontWeight: 600 }}>Models that made calls for this evaluation</div>
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {e.judgeModels.map((m) => (
              <li key={`${m.provider}/${m.model}`} className="sm">
                {m.provider}/{m.model}{m.resolvedModel && m.resolvedModel !== m.model ? ` (${m.resolvedModel})` : ""} — {m.calls} call{m.calls === 1 ? "" : "s"}
                {m.failed > 0 ? <span style={{ color: "var(--warn)" }}>, {m.failed} failed</span> : null}
              </li>
            ))}
          </ul>
          {e.judgeModels.length > 1 && <div className="sm" style={{ color: "var(--warn)" }}>More than one model served this evaluation — a fallback was used for part of it.</div>}
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <div className="sm" style={{ fontWeight: 600 }}>Validation — what the engine changed after the model answered</div>
        {e.validationNotes.length === 0 ? (
          <div className="mut sm">Nothing dropped or corrected.</div>
        ) : (
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {e.validationNotes.map((n, i) => (
              <li key={i} className="sm">{n}</li>
            ))}
          </ul>
        )}
      </div>
      <h4 style={{ margin: "14px 0 6px" }}>Competencies</h4>
      {e.competencies.length === 0 && <div className="mut sm">This evaluation stored no competency results (it failed).</div>}
      {e.competencies.map((c) => {
        const cc = mine.filter((x) => x.competencyId === c.competencyId);
        cc.forEach((x) => claimed.add(x.id));
        return <CompetencyCard key={c.competencyId} c={c} calls={cc} rubric={rubricOf.get(c.competencyId)} />;
      })}
      {(() => {
        const rest = mine.filter((x) => !claimed.has(x.id));
        if (rest.length === 0) return null;
        return (
          <>
            <h4 style={{ margin: "14px 0 6px" }}>Other calls of this evaluation</h4>
            {rest.map((x, i) => (
              <div key={x.id} style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 4 }}>
                <div className="sm">
                  #{i + 1} · {x.provider}/{x.model} · {x.ok ? "ok" : `failed — ${x.error ?? ""}`}{x.competencyId ? ` · ${x.competencyId}` : ""}
                </div>
                <Raw title="Request" value={x.request} />
                <Raw title="Raw response" value={x.response} />
              </div>
            ))}
          </>
        );
      })()}
      {e.recommendation && <div className="mut sm" style={{ marginTop: 10 }}>{e.recommendation}</div>}
    </Modal>
  );
}

/** The evaluation list: one row per evaluation, current marked, Inspect opens the full picture. */
export function EvaluationList({ evaluations, calls }: { evaluations: EvaluationDetail[]; calls: EvalCall[] }): ReactNode {
  const [open, setOpen] = useState<string | null>(null);
  if (evaluations.length === 0) return null;
  const sel = evaluations.find((e) => e.id === open) ?? null;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>Evaluations</h3>
        <span className="tag">newest first · the current one is what the recruiter's report shows · failed runs never replace it</span>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr><th>Standing</th><th>When</th><th>Trigger</th><th>Criteria</th><th>Capability</th><th>Coverage</th><th>Model</th><th>Calls</th><th /></tr>
          </thead>
          <tbody>
            {evaluations.map((e) => {
              const st = standing(e);
              const n = calls.filter((c) => c.evaluationId === e.id).length;
              return (
                <tr key={e.id}>
                  <td><span className={st.cls}>{e.status === "failed" ? "failed" : e.isCurrent ? "current" : "historical"}</span></td>
                  <td className="mut sm">{new Date(e.createdAt).toLocaleString()}</td>
                  <td className="sm">{e.trigger.replace(/_/g, " ")}</td>
                  <td className="tnum">v{e.assessmentSetVersion}{e.outdated ? " ⚠" : ""}</td>
                  <td className="tnum">{e.capability ?? "—"}</td>
                  <td className="tnum">{pct(e.coverage)}</td>
                  <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{e.judgeProvider === "mixed" ? "mixed" : e.judgeModelResolved ?? e.judgeModel ?? "—"}</td>
                  <td className="tnum">{n}</td>
                  <td><button type="button" className="btn sm" onClick={() => setOpen(e.id)}>Inspect</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sel && <EvaluationInspector evaluation={sel} calls={calls} onClose={() => setOpen(null)} />}
    </div>
  );
}
